import { logger } from "@platform/browser-logger";

/**
 * Recovers from "Failed to fetch dynamically imported module" errors.
 *
 * Vite splits the app into content-hashed chunks. After a deploy the old chunk
 * filenames no longer exist on the server, so a user who loaded the page before
 * the deploy hits a 404 the moment they navigate to a route whose JS they hadn't
 * downloaded yet. Vite dispatches `vite:preloadError` on `window` for exactly
 * this case; reloading pulls the fresh index.html and the new chunk names.
 *
 * A reload guard prevents an infinite reload loop when the import keeps failing
 * for a reason a reload can't fix (CDN outage, a genuinely broken chunk). The
 * guard is scoped to the specific asset and only suppresses retries within a
 * short window, so a later deploy — or a different failing chunk — still reloads.
 */
const RELOAD_GUARD_KEY = "vite-preload-reload";
const RELOAD_GUARD_WINDOW_MS = 10_000;

interface ReloadGuard {
  asset: string;
  at: number;
}

const reloadAlreadyAttempted = (asset: string): boolean => {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!raw) {
      return false;
    }
    const guard = JSON.parse(raw) as ReloadGuard;
    const withinWindow = Date.now() - guard.at < RELOAD_GUARD_WINDOW_MS;
    return guard.asset === asset && withinWindow;
  } catch {
    // Storage blocked (e.g. private mode) or a malformed value — treat as no
    // prior attempt so the reload still proceeds. Worst case is one extra
    // reload; a missed reload would leave the user stuck on a broken page.
    return false;
  }
};

const rememberReloadAttempt = (guard: ReloadGuard): void => {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify(guard));
  } catch {
    // Storage blocked or quota exceeded. The guard is best-effort: without it
    // we may reload more than once, but recovery must never be lost — so we
    // swallow here and let the caller reload regardless.
  }
};

export const registerPreloadErrorReload = (): void => {
  window.addEventListener("vite:preloadError", (event) => {
    const error = event.payload;
    const asset = error.message;

    if (reloadAlreadyAttempted(asset)) {
      logger.error(
        "Dynamic import failed again after reload; not retrying",
        error,
        { asset },
      );
      return;
    }

    logger.warn("Dynamic import failed, reloading to load new deploy", {
      asset,
    });

    rememberReloadAttempt({ asset, at: Date.now() });
    window.location.reload();
  });
};
